<script setup lang="ts">
/* Policy Studio layout — a focused workspace shell like settings.vue: its own
   sidebar (back link + the workflow stages) instead of the app-wide nav.
   Queue-backed stages carry a count badge (how much work is piled up), read
   from the registry's stage counts. The dashboard, triage intake and
   initiatives still run on fixture data, so they show only in local dev,
   with a "Concept" badge, until they're wired to the real pipeline. */
import type { NavigationMenuItem } from '@nuxt/ui'
import type { PolicyStage } from '@worker/policy-builder/client'

const isDev = import.meta.dev
const chatAvailable = useChatAvailable()
const { data: counts } = usePolicyCounts()
const pile = (...stages: PolicyStage[]) =>
  stages.reduce(
    (total, stage) => total + (counts.value?.byStage[stage] ?? 0),
    0,
  ) || undefined

const navItems = computed<NavigationMenuItem[][]>(() => [
  [
    {
      label: 'My work',
      icon: 'i-ph-radio-button',
      to: '/policy-studio/my-work',
    },
  ],
  [
    ...(isDev
      ? [
          {
            label: 'Dashboard',
            icon: 'i-ph-squares-four',
            to: '/policy-studio',
            exact: true,
            badge: 'Concept',
          },
        ]
      : []),
    {
      label: 'Policies',
      icon: 'i-ph-stack',
      to: '/policy-studio/policies',
      badge: pile('scoping', 'design', 'validation'),
    },
    ...(isDev
      ? [
          {
            label: 'Triage',
            icon: 'i-ph-caret-circle-up-down',
            to: '/policy-studio/triage',
            badge: 'Concept',
          },
          {
            label: 'Initiatives',
            icon: 'i-ph-target',
            to: '/policy-studio/initiatives',
            badge: 'Concept',
          },
        ]
      : []),
    {
      label: 'Sources',
      icon: 'i-ph-file-text',
      to: '/policy-studio/sources',
    },
    {
      label: 'Scoping',
      icon: 'i-ph-list-checks',
      to: '/policy-studio/scoping',
      badge:
        (pile('scoping') ?? 0) + (counts.value?.waitingOnScoping ?? 0) ||
        undefined,
    },
    {
      label: 'Design',
      icon: 'i-ph-wrench',
      to: '/policy-studio/design',
      badge: pile('design'),
    },
    {
      label: 'Validation',
      icon: 'i-ph-test-tube',
      to: '/policy-studio/validation',
      badge: pile('validation'),
    },
    {
      label: 'Maintenance',
      icon: 'i-ph-heartbeat',
      to: '/policy-studio/maintenance',
    },
  ],
])
</script>

<template>
  <UDashboardGroup class="bg-muted" storage="local" unit="rem">
    <UDashboardSidebar
      class="border-default bg-default"
      :default-size="14"
      :min-size="12"
      :ui="{ footer: 'border-t border-default' }"
      collapsible
      resizable
    >
      <template #toggle>
        <UDashboardSidebarToggle variant="subtle" />
      </template>
      <template #header="{ collapsed }">
        <div class="flex flex-1 items-center gap-2 pl-2">
          <UIcon name="i-ph-pen-nib-bold" class="text-primary" size="lg" />
          <span v-if="!collapsed" class="text-primary font-semibold">
            Policy Studio
          </span>
          <div class="flex-1" />
        </div>
      </template>

      <template #default="{ collapsed }">
        <UNavigationMenu
          :collapsed="collapsed"
          :items="navItems"
          highlight
          highlight-color="primary"
          orientation="vertical"
          variant="pill"
        />
      </template>

      <template #footer="{ collapsed }">
        <div class="flex w-full flex-col">
          <PolicyStudioDevServiceStatus v-if="isDev" :collapsed />
          <div
            class="flex w-full items-center gap-2 px-2 py-2"
            :class="collapsed ? 'justify-center' : 'justify-between'"
          >
            <UButton
              v-if="!collapsed"
              to="/"
              icon="i-ph-caret-left"
              label="Back to admin"
              variant="ghost"
            />
            <UDashboardSidebarCollapse variant="subtle" />
          </div>
        </div>
      </template>
    </UDashboardSidebar>

    <div class="flex w-full min-w-0 flex-col">
      <div
        class="@container-size min-h-0 flex-1 overflow-auto"
        data-scroll-root
      >
        <UDashboardNavbar class="visible lg:hidden" />
        <slot />
      </div>
      <AgentBar v-if="chatAvailable" />
    </div>

    <PolicyStudioBackWithYouPanel />
  </UDashboardGroup>
</template>
